import { META_IMAGES, OG_IMAGES } from '@/v2/constants/images/meta'
import { AUTH_ROUTES, PUBLIC_MAIN_ROUTES } from '@/v2/constants/routes'
import { OGImageType } from '@/v2/types/meta'

export const ogImages: OGImageType[] = [
  {
    url: OG_IMAGES.BASE_URL.concat('/storage-cost-estimate.jpg'),
    alt: 'storage cost estimate page og',
    width: 1200,
    height: 630
  }
]

export const metaImages: OGImageType[] = [
  {
    url: META_IMAGES.DEFAULT,
    alt: 'storage cost estimate page search image',
    width: 92,
    height: 92
  },
  ...ogImages
]

export const heroSectionContent = {
  title: 'Fast <span>Storage</span><span>Cost Estimate Services</span>',
  description:
    "Looking for an affordable storage solution? Use our storage cost estimate tool to get an accurate price based on your storage needs, whether you're storing household items, business inventory, or seasonal gear.",
  image: {
    url: '/images/v2/pages/landing-pages/storage-cost-estimate/hero-image.jpg',
    alt: 'Fast Storage Cost Estimate Services'
  },
  ctaButton: { href: AUTH_ROUTES.REGISTER.href, text: 'Get a Storage Cost Estimate' }
}

export const ourProcessSectionContent = {
  title: 'How the Storage Cost Estimate Works',
  ctaButton: { href: AUTH_ROUTES.REGISTER.href, text: 'Learn More' },

  image: {
    url: '/images/v2/pages/landing-pages/storage-cost-estimate/how-the-storage-cost-estimate-works.png',
    alt: 'How the Storage Cost Estimate Works'
  },

  articles: [
    {
      id: 1,
      title: 'Enter Your Storage Requirements',
      description:
        'Simply fill in the details, such as the size of the unit and the duration of your storage, and receive an instant cost estimate.',
      image: {
        src: '/images/v2/pages/landing-pages/storage-cost-estimate/enter-your-storage-requirements.png',
        alt: 'Enter Your Storage Requirements'
      }
    },
    {
      id: 2,
      title: 'Flexible Pricing Options',
      description:
        'Our tool adjusts prices based on your specific storage needs, helping you find the best option within your budget.',
      image: {
        src: '/images/v2/pages/landing-pages/storage-cost-estimate/flexible-pricing-options.png',
        alt: 'Flexible Pricing Options'
      }
    },
    {
      id: 3,
      title: 'Transparent Pricing',
      description:
        'You’ll receive a clear, breakdown of costs, with no hidden fees, ensuring you know exactly what to expect.',
      image: {
        src: '/images/v2/pages/landing-pages/storage-cost-estimate/transparent-pricing.png',
        alt: 'Transparent Pricing'
      }
    }
  ]
}

export const reliableShipSectionContent = {
  id: 1,
  title: 'Affordable Storage Cost Estimate Solutions',
  description:
    'Affordable storage cost estimates provide clarity for your storage needs by delivering precise pricing information. This helps you plan effectively and eliminates uncertainties associated with renting storage units. By understanding costs upfront, you can make informed decisions that fit your budget and requirements.',
  image: {
    src: '/images/v2/pages/landing-pages/storage-cost-estimate/affordable-storage-cost-estimate-solutions.jpg',
    alt: 'Affordable Storage Cost Estimate Solutions'
  }
}

export const serviceTipsSectionContent = {
  title: 'Benefits of Using Our Storage Cost Estimate Tool',

  cards: [
    {
      id: 1,
      title: 'Affordable Options',
      description:
        'Get an estimate for a variety of storage units, from small lockers to larger spaces, ensuring you find the best price for your budget.',
      image: {
        src: '/images/v2/pages/landing-pages/storage-cost-estimate/affordable-options.png',
        alt: 'Affordable Options'
      }
    },
    {
      id: 2,
      title: 'Quick and Easy',
      description:
        'Save time with an instant cost estimate, allowing you to make an informed decision without any delays.',
      image: {
        src: '/images/v2/pages/landing-pages/storage-cost-estimate/quick-and-easy.png',
        alt: 'Quick and Easy'
      }
    },
    {
      id: 3,
      title: 'No Hidden Fees',
      description:
        'The estimate includes all costs, so you’ll never face unexpected charges or hidden fees when renting your storage unit.',
      image: {
        src: '/images/v2/pages/landing-pages/storage-cost-estimate/no-hidden-fees.png',
        alt: 'No Hidden Fees'
      }
    }
  ]
}

export const helperSectionContent = {
  title: 'Who Should Use a Storage Cost Estimate Tool?',

  articles: [
    {
      content: [
        {
          id: 1,
          title: 'Homeowners and Renters',
          description:
            'Estimate the cost of storing your household items, whether you’re moving or simply need extra space for seasonal items.'
        },
        {
          id: 2,
          title: 'Students',
          description:
            'Get an accurate estimate for storing your dorm or apartment belongings during breaks or between terms.'
        },
        {
          id: 3,
          title: 'Business Owners',
          description:
            'Estimate costs for storing inventory, office equipment, or important documents in a secure, accessible unit.'
        }
      ],
      image: {
        src: '/images/v2/pages/landing-pages/storage-cost-estimate/who-should-use-a-storage-cost-estimate-tool.jpg',
        alt: 'Who Should Use a Storage Cost Estimate Tool?'
      }
    }
  ]
}

export const benefitsSectionContent = {
  title: 'Explore Our Storage Services',

  cards: [
    {
      id: 1,
      title: 'Business Storage Solutions',
      description:
        'Estimate the cost of storing business equipment, inventory, or documents with flexible, affordable options.',
      image: {
        src: '/images/v2/pages/common/business-storage-solution.png',
        alt: 'Business Storage Solutions'
      },
      link: { href: AUTH_ROUTES.REGISTER.href, text: 'Explore Business Storage' }
    },
    {
      id: 2,
      title: 'Moving Storage Solution',
      description:
        'Get an accurate estimate for storing your belongings during a move, ensuring a seamless and stress-free transition.',
      image: {
        src: '/images/v2/pages/common/moving-storage-solution.png',
        alt: 'Moving Storage Solution'
      },
      link: { href: AUTH_ROUTES.REGISTER.href, text: 'Explore Moving Storage' }
    },
    {
      id: 3,
      title: 'Luggage and Box Shipping Services',
      description:
        'Use our cost estimate tool for storage and shipping of luggage or boxes, whether locally or internationally.',
      image: {
        src: '/images/v2/pages/common/luggage-and-box-shipping-services.png',
        alt: 'Luggage and Box Shipping Services'
      },
      link: { href: AUTH_ROUTES.REGISTER.href, text: 'Explore Box Storage' }
    },
    {
      id: 4,
      title: 'Residential Storage Services',
      description:
        'Affordable storage options for homeowners looking to store extra furniture, seasonal items, or personal belongings.',
      image: {
        src: '/images/v2/pages/common/residential-storage-services.png',
        alt: 'Residential Storage Services'
      },
      link: { href: AUTH_ROUTES.REGISTER.href, text: 'Explore Residential Storage' }
    }
  ]
}

export const whyChooseSectionContent = {
  title: 'Why Choose ShipAndStorage for Your Storage Needs?',

  image: {
    url: '/images/v2/pages/landing-pages/storage-cost-estimate/why-choose-shipandstorage-for-your-storage-needs.jpg',
    alt: 'Why Choose ShipAndStorage for Your Storage Needs?'
  },

  articles: [
    {
      id: 1,
      title: 'Competitive Rates',
      description:
        'Our storage cost estimate tool ensures you receive the best rates available, helping you stick to your budget.'
    },
    {
      id: 2,
      title: '24/7 Secure Access',
      description:
        'Our units are equipped with advanced security features, giving you peace of mind that your items are safe.'
    },
    {
      id: 3,
      title: 'Transparent Pricing',
      description:
        'Get a full breakdown of costs with no hidden charges or extra fees. We believe in keeping things simple and clear.'
    },
    {
      id: 4,
      title: 'Flexible Rental Terms',
      description: 'Rent storage units for as short or long as you need, with no long-term commitments required.'
    },
    {
      id: 5,
      title: 'Variety of Storage Options',
      description: 'Whether you need a small unit or a larger space, we offer flexible options to match your needs.'
    },
    {
      id: 6,
      title: 'Exceptional Customer Support',
      description:
        'Our team is here to help with any questions or concerns, making the storage process hassle-free for you.'
    }
  ]
}

export const promotionalBannerSectionContent = {
  id: 1,
  title: 'Get a Storage Cost Estimate Fast and Easily Today',
  description:
    'Get a storage cost estimate fast and easy. Accurate, affordable solutions to help you estimate the cost of storing your belongings.',
  image: {
    src: '/images/v2/pages/landing-pages/storage-cost-estimate/get-a-storage-cost-estimate-fast-and-easily-today.png',
    alt: 'Get a Storage Cost Estimate Fast and Easily Today'
  },
  ctaButton: { href: PUBLIC_MAIN_ROUTES.MOVING_BOX_STORAGE.href, text: 'Get Your Estimate Today' }
}

export const ourPartnersSectionContent = {
  description:
    'ShipAndStorage partners with trusted logistics providers to ensure your items are securely stored and easily accessible.',

  cards: [
    {
      id: 1,
      title: 'FedEx',
      description: [
        'FedEx offers reliable shipping and storage solutions, ensuring your items are safely stored and delivered.',
        "<strong>Disclaimer:</strong> This page was last updated in September 2024 for reference only. FedEx does not guarantee the accuracy or completeness of the information provided. For more information, visit <a href='https://www.fedex.com/en-us/home.html' target='_blank'>FedEx.com</a>. FedEx® is a registered trademark. © 1995-2024 FedEx."
      ]
    },
    {
      id: 2,
      title: 'DHL',
      description: [
        'DHL provides global shipping and storage solutions, ensuring your belongings are safely stored and shipped to your destination.',
        "<strong>Disclaimer:</strong> This page was last updated in September 2024 for reference only. DHL does not guarantee the accuracy of the information provided. For more information, visit <a href='https://www.dhl.com/global-en/home.html' target='_blank'>DHL.com</a>. DHL® is a registered trademark of DHL International GmbH. All rights reserved."
      ]
    },
    {
      id: 3,
      title: 'UPS',
      description: [
        'UPS delivers top-notch storage services with guaranteed security, helping you store and ship your items hassle-free.',
        "<strong>Disclaimer:</strong> This page was last updated in September 2024 for reference only. UPS does not guarantee the accuracy or completeness of the information provided. For more information, visit <a href='https://www.ups.com/' target='_blank'>UPS.com</a>. UPS® is a registered trademark of UPS Inc. All rights reserved."
      ]
    }
  ]
}
